// TEMPLATE: Content layer — components must use useContent() hook, never import content files directly.
// This context provides locale-aware content via getContent(site, lang).
import { createContext, useContext } from 'react'
import { getContent } from '../content'
import { useSite } from './SiteContext'

const ContentContext = createContext(null)

/**
 * ContentProvider - Loads content based on current site and language
 *
 * Single Source of Truth for:
 * - Dynamic content loading (reacts to site/lang changes)
 * - Content distribution to all components
 *
 * Automatically reloads content when site or language changes via SiteContext
 */
export function ContentProvider({ children }) {
  const { currentSite, currentLang } = useSite()

  // Sync content in the same render as site/lang — NOT in useEffect.
  // Delayed setState caused a one-frame mismatch: sectionsOrder already
  // switched (e.g. mentorship → regression) while content was still the
  // previous site → PodcastVideoSection crashed on podcasts[0].label.
  const content = getContent(currentSite, currentLang)

  return (
    <ContentContext.Provider value={content}>
      {children}
    </ContentContext.Provider>
  )
}

/**
 * useContent Hook
 *
 * Provides access to current site content
 *
 * @returns {Object} Content object with all site content (header, footer, sections, etc.)
 */
export function useContent() {
  const context = useContext(ContentContext)
  if (context === null) throw new Error('useContent must be used within ContentProvider')
  return context
}
